import { skuRequiresImeiIccid } from "@integrator-src/manufacturing/configuration-sku-family";
import {
  salesOrderRegionField,
  type SalesOrderRegionField,
} from "@integrator-src/sales-order/sales-order-address-country";

import { isInvalidLineItemQuantity } from "./createSalesOrderPageUi";

export type SalesOrderApprovePartyOption = {
  fsAccountId?: string | null;
  salesManagerError?: string | null;
  repAgencyError?: string | null;
  repStaffWarning?: string | null;
};

export type SalesOrderApproveLineItem = {
  sku: string;
  unitPrice: string;
  quantity: string;
};

export type SalesOrderApproveValidationInput = {
  isPendingOrder: boolean;
  customerPurchaseOrderNumber: string;
  shipByDate: string;
  clientOption: SalesOrderApprovePartyOption | null;
  endUserOption: SalesOrderApprovePartyOption | null;
  items: SalesOrderApproveLineItem[];
  shipToCountry: string;
  shipToState: string;
  shipToAddressLine1: string;
  shipToCity: string;
  shipToZip: string;
};

function missingShipToRegionLabel(regionField: SalesOrderRegionField): string {
  return `Ship-to ${regionField.regionLabel.toLowerCase()}`;
}

function missingShipToPostalLabel(regionField: SalesOrderRegionField): string {
  return `Ship-to ${regionField.postalLabel.toLowerCase()}`;
}

export function listSalesOrderApproveValidationMessages(input: SalesOrderApproveValidationInput): string[] {
  if (!input.isPendingOrder) {
    return [];
  }

  const messages: string[] = [];

  const missingRequiredHeaderFields = [
    input.customerPurchaseOrderNumber.trim() === "" ? "Customer PO" : null,
    input.shipByDate.trim() === "" ? "Ship by date" : null,
    input.clientOption == null ? "Client" : null,
    input.endUserOption == null ? "End user" : null,
  ].filter((field): field is string => field != null);

  if (missingRequiredHeaderFields.length > 0) {
    messages.push(`Add ${missingRequiredHeaderFields.join(", ")} before approving.`);
  }

  const repBlockingMessages = [
    input.endUserOption?.salesManagerError ?? null,
    input.endUserOption?.repAgencyError ?? null,
  ].filter((message): message is string => Boolean(message));
  messages.push(...repBlockingMessages);

  const hasWirelessLineItems = input.items.some((item) => skuRequiresImeiIccid(item.sku));
  if (
    hasWirelessLineItems &&
    (input.endUserOption?.fsAccountId == null || input.endUserOption.fsAccountId.trim() === "")
  ) {
    messages.push(
      "End user Freshsales account id is required before approving a sales order with wireless units.",
    );
  }

  const regionField = salesOrderRegionField(input.shipToCountry);
  const missingShipToFields = [
    input.shipToAddressLine1.trim() === "" ? "Ship-to address line 1" : null,
    input.shipToCity.trim() === "" ? "Ship-to city" : null,
    input.shipToState.trim() === "" && regionField.kind === "select"
      ? missingShipToRegionLabel(regionField)
      : null,
    input.shipToZip.trim() === "" ? missingShipToPostalLabel(regionField) : null,
  ].filter((field): field is string => field != null);

  if (missingShipToFields.length > 0) {
    messages.push(`Add ${missingShipToFields.join(", ")} before approving.`);
  }

  const lineItemsMissingSku = input.items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.sku.trim() === "")
    .map(({ index }) => index + 1);
  if (lineItemsMissingSku.length > 0) {
    messages.push(`Add a SKU for line ${lineItemsMissingSku.join(", ")}.`);
  }

  const lineItemsMissingUnitPrice = input.items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.unitPrice.trim() === "")
    .map(({ index }) => index + 1);
  if (lineItemsMissingUnitPrice.length > 0) {
    messages.push(
      `Add a unit price for line ${lineItemsMissingUnitPrice.join(", ")}. Empty is not allowed; use 0 if the item has no charge.`,
    );
  }

  const lineItemsInvalidQuantity = input.items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isInvalidLineItemQuantity(item.quantity))
    .map(({ index }) => index + 1);
  if (lineItemsInvalidQuantity.length > 0) {
    messages.push(`Set quantity to at least 1 for line ${lineItemsInvalidQuantity.join(", ")}.`);
  }

  return messages;
}

export function formatSalesOrderApproveValidationMessage(messages: string[]): string {
  return messages.join(" ");
}
